import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {useRampFlagCoreweaveIssues} from '../../state/issues/useCoreweaveIssues';
import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import {useRampFlagFullScreenLinePlotEditing} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {AddPanels} from './AddPanels/AddPanels';
import {WorkspaceCreateTriggerDrawerWrapper} from './CreateTrigger/WorkspaceCreateTriggerDrawerWrapper';
import {LinePlotPanelEditorWrapper} from './EditLinePlotPanel/LinePlotPanelEditorWrapper';
import {FloatingWorkspaceDrawer} from './FloatingWorkspaceDrawer';
import {PanelsQuickAdd} from './PanelsQuickAdd/PanelsQuickAdd';
import {RunIssuesDrawer} from './RunIssues/RunIssuesDrawer';
import {EvaluationTablesSettingsDrawer} from './Settings/evaluationTables/EvaluationTablesSettingsDrawer';
import {MarimoSettings} from './Settings/marimo/MarimoSettings';
import {MediaSettingsDrawer} from './Settings/media/MediaSettingsDrawer';
import {SectionSettings} from './Settings/SectionSettings';
import {WorkspaceSettingsContent} from './Settings/WorkspaceSettingsContent';
import {useWorkspaceDrawerContext} from './WorkspaceDrawerContextProvider';

export const WorkspaceDrawer = () => {
  const {closeWorkspaceDrawer, contentType} = useWorkspaceDrawerContext();

  const isDrawerOpen = contentType != null;

  return (
    <Tailwind>
      <FloatingWorkspaceDrawer
        isDrawerOpen={isDrawerOpen}
        closeWorkspaceDrawer={closeWorkspaceDrawer}>
        <div
          className="h-full w-full"
          data-test="workspace-drawer"
          // Unmask the settings drawer (all levels) in replay so support can see
          // which setting changed — workspace config, not credentials/account data.
          data-dd-privacy={DD_PRIVACY_ALLOW}>
          <WorkspaceDrawerContent />
        </div>
      </FloatingWorkspaceDrawer>
    </Tailwind>
  );
};

export const WorkspaceDrawerContent = () => {
  const {contentType, delayedContentType, targetPanelRef, targetSectionRef} =
    useWorkspaceDrawerContext();
  const isIssuesObservabilityEnabled = useRampFlagCoreweaveIssues();
  const isFullScreenLinePlotEditingEnabled =
    useRampFlagFullScreenLinePlotEditing();
  // to ensure the drawer contents don't abruptly disappear during the
  // "drawer closing" transition, we must use delayedContentType so the
  // contents remain visible until the drawer is fully closed.
  const displayedContentType = contentType ?? delayedContentType;

  if (displayedContentType === 'add-panels') {
    return <AddPanels />;
  }

  if (displayedContentType === 'run-issues' && isIssuesObservabilityEnabled) {
    return <RunIssuesDrawer />;
  }

  if (displayedContentType === 'panels-quick-add') {
    return <PanelsQuickAdd />;
  }

  if (displayedContentType === 'workspace-settings') {
    return (
      <WorkspaceSettingsContent displayedContentType={displayedContentType} />
    );
  }

  if (
    displayedContentType === 'media-settings' &&
    targetPanelRef != null &&
    targetSectionRef != null
  ) {
    return (
      <MediaSettingsDrawer
        panelRef={targetPanelRef}
        panelBankSectionConfigRef={targetSectionRef}
      />
    );
  }

  if (
    displayedContentType === 'evaluation-tables-settings' &&
    targetPanelRef != null &&
    targetSectionRef != null
  ) {
    return (
      <EvaluationTablesSettingsDrawer
        panelRef={targetPanelRef}
        panelBankSectionConfigRef={targetSectionRef}
      />
    );
  }

  if (displayedContentType === 'section-settings') {
    if (targetSectionRef == null) {
      // This shouldn't happen, since we're in the section settings drawer
      return null;
    }
    return (
      <SectionSettings
        displayedContentType={displayedContentType}
        sectionRef={targetSectionRef}
      />
    );
  }

  if (displayedContentType === 'create-trigger') {
    return <WorkspaceCreateTriggerDrawerWrapper />;
  }

  if (
    displayedContentType === 'line-plot-editor' &&
    targetPanelRef != null &&
    isFullScreenLinePlotEditingEnabled
  ) {
    return <LinePlotPanelEditorWrapper panelRef={targetPanelRef} />;
  }

  if (
    displayedContentType === 'marimo-settings' &&
    targetPanelRef != null &&
    targetSectionRef != null
  ) {
    return (
      <MarimoSettings
        panelRef={targetPanelRef}
        panelBankSectionConfigRef={targetSectionRef}
      />
    );
  }

  return null;
};
